import React from 'react';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { reverse } from 'named-urls';

import Grid, { defaultRenderers, defaultOperators, defaultFormatters } from 'components/Grid';

import { ColumnDefinition } from 'components/Grid/types';

import { EntityModules, ProtectorType } from 'core/constants';

import { useStore } from 'utils/hooks/store';

import routes from 'core/routes';

import API from 'utils/api';
import { TemplateSearchResponse, UuidableName } from 'utils/api/types';
import { listRenderer } from 'components/Grid/renderers/GridListRenderer';
import Search from 'components/Grid/operators/Search';

import { ScheduleDetailsResponse } from 'utils/api/modules/schedules/schedules.types';

import { formatDateForDisplay } from 'utils/formatters/time';
import { formatMoment, formatMomentInZone } from 'utils/formatters/timezone';
import { timezoneStore } from 'stores/Timezone';
import { StyledCreated } from 'containers/Sites/components/SitesTable/styles';
import { getEntityGridColumns } from 'components/Grid/components/EntitiesGridColumn/EntitiesGridColumn';

import { ScheduleStatusOperator } from 'components/Grid/operators/Status';
import ScheduleStatusChip from 'components/Chips/ScheduleStatusChip';

import { frequencyRenderer } from './utils';
import { assignedToFormatter, assignedToItemRenderer, assignedToValueGetter } from './AssignedToCell';
import ActionCell from './ActionCell';

// Module-scoped so each operator array keeps a stable identity across renders.
const LIST_MOMENT_FORMAT = 'DD MMM YYYY, HH:mm';

const workflowsTemplateFilterOperators = Search<TemplateSearchResponse>(
  (params) => API.workflows.searchTemplates(params),
  (uuid: string) => API.workflows.loadTemplateDetails(uuid),
  'name'
);

// A flag-on org can still hold a saved filter whose uuid is a workflows template, and the operator
// refetches forever when a lookup 404s — so fall back, mirroring the backend's match-either filter.
const loadAuditFormOrTemplate = (uuid: string) => async () => {
  try {
    return await API.entities.loadEntityForm('audits', uuid)();
  } catch {
    return API.workflows.loadTemplateDetails(uuid)();
  }
};

const auditFormFilterOperators = Search<UuidableName>(
  (params) => API.entities.searchForms('audits', { ...params, ordering: 'name' }),
  loadAuditFormOrTemplate,
  'name'
);

type SchedulesTableProps = {
  // oxlint-disable-next-line typescript/no-explicit-any
  onError: (error: any) => void;
  onRefreshCallback: (callback: () => void) => void;
};

// Default columns:
// Name, Audit template, Assigned to, Frequency

// Optional columns:
// Site, Asset, Contact, Created by, Created date

export const buildBasicScheduleColumns = (auditsAsEntity: boolean) =>
  [
    {
      field: 'name',
      headerName: 'Name',
      protectorType: ProtectorType.ShortText,
      nativeFilter: 'name',
    },
    {
      field: 'template',
      headerName: 'Audit template',
      nativeFilter: 'template',
      filterOperators: auditsAsEntity ? auditFormFilterOperators : workflowsTemplateFilterOperators,
      renderCell: ({ value }: { value: UuidableName }) => <p>{value.name}</p>,
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned to',
      filterOperators: defaultOperators.user(),
      valueGetter: assignedToValueGetter,
      renderCell: listRenderer(assignedToItemRenderer, 1),
      valueFormatter: assignedToFormatter,
      nativeFilter: 'assignedTo',
    },
    {
      field: 'recurrence',
      headerName: 'Frequency',
      sortable: false,
      filterable: false,
      filterOperators: null,
      renderCell: ({ value }: { value: string }) => frequencyRenderer({ value }),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      renderCell: ({ row }: { row: ScheduleDetailsResponse }) => (
        <ScheduleStatusChip isPaused={row.isPaused} pauseReason={row.pauseReason} />
      ),
      filterOperators: ScheduleStatusOperator(),
      nativeFilter: true,
    },
    // hidden
    {
      defaultVisibility: false,
      field: 'nextDue',
      headerName: 'Next due',
      valueGetter: (value: unknown) => ({ value }),
      renderCell: ({ value, row }: { value: { value: string | null }; row: ScheduleDetailsResponse }) => {
        if (!value.value) return <StyledCreated>—</StyledCreated>;
        return (
          <StyledCreated>
            {timezoneStore.enabled
              ? formatMomentInZone(value.value, row.timezone, LIST_MOMENT_FORMAT)
              : formatDateForDisplay(value.value)}
          </StyledCreated>
        );
      },
      valueFormatter: defaultFormatters.date,
      filterable: false,
      filterOperators: null,
    },
    {
      defaultVisibility: false,
      field: 'createdBy',
      headerName: 'Created by',
      renderCell: defaultRenderers.user,
      valueFormatter: defaultFormatters.user,
      filterOperators: defaultOperators.user(),
      valueGetter: (value: unknown) => ({ value }),
    },
    {
      defaultVisibility: false,
      field: 'createdAt',
      headerName: 'Created date',
      valueGetter: (value: unknown) => ({ value }),
      renderCell: ({ value }: { value: { value: string } }) => (
        <StyledCreated>{formatMoment(value.value, LIST_MOMENT_FORMAT)}</StyledCreated>
      ),
      filterOperators: defaultOperators.date_time(),
      valueFormatter: defaultFormatters.date,
      nativeFilter: 'createdAt',
      protectorType: ProtectorType.DateTime,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      maxWidth: 50,
      ariaLabel: 'actions_cell',
      renderCell: ({ row, colDef }: { row: ScheduleDetailsResponse; colDef: ColumnDefinition }) => (
        <ActionCell rowData={row} actions={colDef.actions} />
      ),
      filterOperators: null,
      sortable: false,
    },
  ] as unknown as ColumnDefinition[];

const SchedulesTable: React.FunctionComponent<SchedulesTableProps> = ({
  onError,
  onRefreshCallback,
}): React.ReactElement => {
  const {
    routing,
    session: { user },
  } = useStore();

  const onRowClick: GridEventListener<'rowClick'> = (item: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.schedules.details, { scheduleId: item.id }));
  };

  // get only sites, contacts and assets
  const entityModules = user?.activeEntityModules
    ? user.activeEntityModules.filter((module) =>
        [EntityModules.Sites, EntityModules.Contacts, EntityModules.Assets].includes(module)
      )
    : [];

  // The flag, not `activeEntityModules`: this picks the filter's data source, and the backend
  // gates that on the flag alone. Nav placement (breadcrumbs, sidebar) keys off the module list.
  const auditsAsEntity = !!user?.auditsAsEntityEnabled;

  return (
    <Grid
      basicColumns={[
        ...buildBasicScheduleColumns(auditsAsEntity),
        ...getEntityGridColumns(entityModules, false, false, false),
      ]}
      entity="schedules"
      onError={onError}
      dataURL="/api/schedules/"
      disableSelectAllColumns
      customNoRowsText="No schedules have been created yet"
      onRowClick={onRowClick}
      onRefreshCallback={onRefreshCallback}
      disableExport
    />
  );
};

export default SchedulesTable;
